import React, { useState, useEffect, useRef } from "react";
import { ActionIcon, Button, CloseButton, Paper, Stack, TextInput } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";
import axiosInstance from './../axiosInstance.js';



// We deal with tags as strings only.
// TODO: remove initFocus
// TODO (!!!): deal with tags as objects, not strings. It will allow to add more info to tags, like color, type, etc.
const TagsInput = ({ 
  initialTags = [], // required
  onTagsChange, 
  editable = true,
  language, // required 
  reducedView = false, // For reduced view (only global manual tags shown)
  initialTagObjects = [],
 }) => {
  const [tags, setTags] = useState(initialTags); // Initialize with array of strings
  const [searchResults, setSearchResults] = useState([]); // For auto-complete
  const [searchQuery, setSearchQuery] = useState(""); // Input field query
  const [isDropdownVisible, setIsDropdownVisible] = useState(false); // Manage dropdown visibility (for search)
  const [selectedGlobalManualTag, setSelectedGlobalManualTag] = useState(initialTags.find(isGlobalManualTag)); // State for selected global manual tag
  const [isInputVisible, setIsInputVisible] = useState(false); // State to toggle input visibility
  const [isGlobalManualTagDropdownVisible, setIsGlobalManualTagDropdownVisible] = useState(false); // State to toggle global manual tag dropdown

  // TODO: make it part of is global automatic tag
  const genreTagsNames = initialTagObjects.filter(tag => tag.type === 5).map(tag => tag.name);

  const inputRef = useRef(null); // Ref for the input element
  const dropdownRef = useRef(null); // Ref for the search dropdown menu
  const globalManualTagDropdownRef = useRef(null); // Ref for the global manual tag dropdown

  // const [initFocusRelevant, setInitFocusRelevant] = useState(true);
  // useEffect(() => {
  //   if (inputRef.current && initFocus && initFocusRelevant) {
  //     inputRef.current.focus();
  //     setInitFocusRelevant(false); // is it really needed here?
  //   }
  // });

  const addTag = (tag) => {
    let updatedTags = [...tags];

    if (isGlobalManualTag(tag)) {
      // Remove previous global manual tag.
      updatedTags = updatedTags.filter(tagName => !isGlobalManualTag(tagName));
    }

    let customTags = updatedTags.filter(tagName => !isGlobalManualTag(tagName) && !isGlobalAutomaticTag(tagName));

    if (customTags.length === 8){
      let txt = "Maximum 8 custom tags";
      if (language === "ru") {
        txt = "Максимум 8 собственных тэгов тэгов";
      }
      alert(txt);
      return;
    }

    if (!updatedTags.includes(tag)) { // Check if tag is already included
      updatedTags = [...updatedTags, tag];
      setTags(updatedTags);
      onTagsChange(updatedTags); // Notify the parent of updated tags
    }
  };

  const removeTag = (tag) => {
    const updatedTags = tags.filter((t) => t !== tag);
    setTags(updatedTags);
    onTagsChange(updatedTags); // Notify the parent of updated tags
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length > 0 || true) {
      try {
        const response = await axiosInstance.get(`/relevant/tags/search?query=${query}`);
        setSearchResults(response.data.map(item => item.name));
        setIsDropdownVisible(true); // Show the dropdown when there are results
      } catch (error) {
        console.error("Error fetching search results:", error);
      }
    } else {
      setSearchResults([]); // Clear search results if input is empty
      setIsDropdownVisible(false); // Hide the dropdown if there are no results
    }
  };

  const handleAddTag = (tag) => {
    addTag(tag);
    setSearchQuery(""); // Clear the search input after adding
    setSearchResults([]); // Clear the search results after adding
    setIsDropdownVisible(false); // Hide the dropdown after adding
  };

  const handleInputChange = (e) => {    
    handleSearch(e.target.value);
  };

  const handleDropdownItemClick = (tag) => {
    handleAddTag(tag);
    setIsDropdownVisible(false);
    // setInitFocusRelevant(false);
  };

  const handleGlobalManualTagSelection = (tag) => {
    setSelectedGlobalManualTag(tag);
    addTag(tag); // Add the selected global manual tag
  };

  const handleClickOutside = (event) => {
    // Hide the search dropdown if clicking outside
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) 
      // inputRef.current &&
      // !inputRef.current.contains(event.target)
    ) {
      setIsDropdownVisible(false);
    }

    // Hide the global manual tag dropdown if clicking outside
    if (
      globalManualTagDropdownRef.current &&
      !globalManualTagDropdownRef.current.contains(event.target)
    ) {
      setIsGlobalManualTagDropdownVisible(false);
    }
  };

  useEffect(() => {
    // Add event listener for clicks outside the component
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  return (
    <div className="relative px-2 md:px-4">
      {/* Display global manual tags and added tags */}
      <div className="flex justify-center flex-wrap items-center gap-1">
        {editable ? (
          initialTags.length > 0 && selectedGlobalManualTag ? (
            <div className="relative" ref={globalManualTagDropdownRef}>
              <TagBadge
                component="button"
                type="button"
                className="cursor-pointer"
                radius="md"
                size="md"
                color={tagBadgeColor(selectedGlobalManualTag)}
                variant="filled"
                rightSection={<span className="text-[10px] opacity-70">▼</span>}
                onClick={() =>
                  setIsGlobalManualTagDropdownVisible(!isGlobalManualTagDropdownVisible)
                }
              >
                {tagName(selectedGlobalManualTag, language)}
              </TagBadge>
              {isGlobalManualTagDropdownVisible && (
                <Paper
                  withBorder
                  shadow="md"
                  radius="md"
                  p={4}
                  className="absolute top-full left-0 mt-1 z-50"
                  style={{ width: "max-content" }}
                >
                  <Stack gap={4}>
                    {["like", "veryGood", "good", "finished", "bad", "inProgress", "todo"]
                      .filter((tag) => tag !== selectedGlobalManualTag) // Exclude the currently selected tag
                      .map((tag, index) => (
                        <TagBadge
                          key={index}
                          component="button"
                          type="button"
                          radius="md"
                          size="md"
                          className="cursor-pointer"
                          color={tagBadgeColor(tag)}
                          variant="filled"
                          onClick={() => {
                            setSelectedGlobalManualTag(tag); // Update selected tag
                            addTag(tag); // Add the selected tag
                            setIsGlobalManualTagDropdownVisible(false); // Hide dropdown
                          }}
                        >
                          {tagName(tag, language)}
                        </TagBadge>
                      ))}
                  </Stack>
                </Paper>
              )}
            </div>
          ) : (
            ["like", "veryGood", "good", "finished", "bad", "inProgress", "todo"].map((tag, index) => (
              <TagBadge
                key={index}
                component="button"
                type="button"
                radius="md"
                size="md"
                className="cursor-pointer"
                color={selectedGlobalManualTag === tag ? tagBadgeColor(tag) : "gray"}
                variant={selectedGlobalManualTag === tag ? "filled" : "light"}
                onClick={() => {
                  setSelectedGlobalManualTag(tag);
                  addTag(tag); // Add the selected tag
                }}
              >
                {tagName(tag, language)}
              </TagBadge>
            ))
          )
        ) : (
          selectedGlobalManualTag && (
            <TagBadge
              radius="md"
              size="md"
              color={tagBadgeColor(selectedGlobalManualTag)}
              variant="filled"
            >
              {tagName(selectedGlobalManualTag, language)}
            </TagBadge>
          )
        )}

        {!reducedView &&
        <>
          {/* Display added tags */}
          {tags.filter(tag => !isGlobalManualTag(tag) && !isGlobalAutomaticTag(tag) && !genreTagsNames.includes(tag)).map((tag, index) => (
            <TagBadge
              key={index}
              component="span"
              radius="md"
              size="md"
              color={tagBadgeColor(tag)}
              variant="light"
            >
              {
              tag.includes("\\")
              ?
                <div className="flex items-center text-center gap-1">
                  #
                  <img className="w-5 max-w-none h-5 rounded-full" src={tag.split("\\")[1]} />
                  {tagName(tag.split("\\")[0], language)}                
                </div>
              :
                <>
                  {tagName(tag, language)}
                </>
              }

              {
              (!isGlobalAutomaticTag(tag) && editable) &&
              <CloseButton
                size="xs"
                className="ml-1"
                onClick={(event) => {
                  event.stopPropagation();
                  removeTag(tag);
                }}
              />
              }
            </TagBadge>
          ))}

          {/* Add "+" button */}
          {(editable && selectedGlobalManualTag) && (
            <ActionIcon
              size="lg"
              radius="md"
              variant="light"
              color="gray"
              aria-label="Add tag"
              onClick={() => setIsInputVisible(!isInputVisible)} // Toggle input visibility
            >
              +
            </ActionIcon>
          )}
        </>
        }
      </div>

      {/* Input field */}
      {isInputVisible && editable && (
        <div className="mt-1 w-full flex justify-center"> {/* Input on a new line */}
          <TextInput
            ref={inputRef}
            type="text"
            inputMode="search"
              radius="md"
              size="md"
              className="w-auto text-center"
            styles={{ input: { textAlign: "center", fontSize: 14, lineHeight: 1.4, paddingBlock: 8 } }}
            placeholder={language === "ru" ? "Введите название тэга" : "Enter tag name"}
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchQuery.trim() !== "") {
                handleAddTag(searchQuery.trim()); // Add tag on Enter key press
                setIsInputVisible(false); // Hide input after adding
              }
            }}
            onBlur={() => setIsInputVisible(false)} // Hide input on blur
          />
        </div>
      )}

      {/* Search Results Dropdown */}
      {isDropdownVisible && searchResults.length > 0 && (
        <Paper
          ref={dropdownRef}
          withBorder
          shadow="md"
          radius="md"
          className="absolute top-full left-0 right-0 mt-1 max-h-40 overflow-y-auto z-[60]"
        >
          <Stack gap="xs" p="xs">
            {searchResults.map((tag, index) => (
                <Button
                  key={index}
                  type="button"
                  size="sm"
                  variant="subtle"
                  color={tagBadgeColor(tag)}
                  radius="md"
                  onMouseDown={() => handleDropdownItemClick(tag)} /* for some f*** unknown reason onclick doesn't work! */
                >
                {tagName(tag, language)}
              </Button>
            ))}
          </Stack>
        </Paper>
      )}
      
    </div>
  );
};

// Define isGlobalManualTag before useState
const isGlobalManualTag = (name) => {
  return ["like", "todo", "finished", "veryGood", "good", "bad", "inProgress"].includes(name);
};

// It should be handled in backend.
const isGlobalAutomaticTag = (name) => {
  if (name === "book" || name === "movie" || name === "game" || name === "youtubechannel") {
    return true;
  }
  return false;
}

export const tagColor = (name, rounded = true, bgColorOverride, type = 0) => {
  const base = `font-sans font-medium tracking-wide text-[11px] px-2.5 py-0.5 
               ${rounded ? "rounded-full" : ""}`;

  if (bgColorOverride) {
    return `${base} ${bgColorOverride} text-white`;
  }

  if (name === "like") {
    return `${base} bg-red-500/90 text-white shadow-sm`;
  }
  if (name === "veryGood") {
    return `${base} bg-orange-500/90 text-white shadow-sm`;
  }
  if (name === "good") {
    return `${base} bg-green-500/90 text-white shadow-sm`;
  }
  if (name === "inProgress") {
    return `${base} bg-cyan-500/90 text-white shadow-sm`;
  }
  if (name === "todo") {
    return `${base} bg-sky-300/90 text-white shadow-sm`;
  }
  if (name === "finished") {
    return `${base} bg-yellow-500/90 text-white shadow-sm`;
  }
  if (name === "bad") {
    return `${base} bg-gray-600/90 text-white shadow-sm`;
  }

  if (name === "book" || name === "movie" || name === "game" || name === "youtubechannel") {
    return `${base} bg-teal-500/80 text-white`;
  }

  if (type === 5) {
    return `${base} bg-purple-500/90 text-white shadow-sm`;
  }

  return `${base} bg-gray-500 text-white`;
};

export default TagsInput;
